import classNames from "classnames"
import "./Table.css"
import React, {
    createContext,
    forwardRef,
    HTMLAttributes,
    TableHTMLAttributes,
    TdHTMLAttributes,
    ThHTMLAttributes,
    useCallback,
    useContext,
    useState,
} from "react"
import { createPortal } from "react-dom"
import { useObserveElementRect } from "@nova/toolkit-pure-hooks"
import * as Icons from "@nova/component-icons"

const TableContext = createContext<{
    headRef: (el: HTMLElement | null) => void
    footerPortalEl: HTMLDivElement | null
} | null>(null)

function TableRoot({ children, className, ...props }: TableHTMLAttributes<HTMLTableElement>) {
    const { ref: headRef, height: headHeight } = useObserveElementRect()
    const [footerPortalEl, setFooterPortalEl] = useState<HTMLDivElement | null>(null)
    const footerPortalRef = useCallback((el: HTMLDivElement | null) => setFooterPortalEl(el), [])

    return (
        <TableContext.Provider value={{ headRef, footerPortalEl }}>
            <div
                className={classNames(
                    "w-full h-full border rounded-[16px] border-gray-700 overflow-hidden flex flex-col",
                    className
                )}
            >
                <div
                    className="flex-1 min-h-0 w-full overflow-auto table-scroll relative"
                    style={
                        {
                            "--header-height": `${headHeight}px`,
                        } as React.CSSProperties
                    }
                >
                    <table className="w-full h-full border-separate border-spacing-0" {...props}>
                        {children}
                    </table>
                </div>
                <div ref={footerPortalRef} />
            </div>
        </TableContext.Provider>
    )
}

const TableRootPure = ({
    children,
    className,
    ...props
}: TableHTMLAttributes<HTMLTableElement>) => (
    <div
        className={classNames(
            "w-full h-full border rounded-[16px] border-gray-700 overflow-hidden flex flex-col",
            className
        )}
    >
        <div className="flex-1 min-h-0 w-full overflow-auto table-scroll relative">
            <table className="w-full h-full border-separate border-spacing-0" {...props}>
                {children}
            </table>
        </div>
    </div>
)

const Head = ({
    children,
    className,
    style,
    ...props
}: HTMLAttributes<HTMLTableSectionElement>) => {
    const ctx = useContext(TableContext)
    return (
        <thead
            ref={ctx?.headRef}
            className={classNames("text-gray-400 sticky top-0 z-10 -translate-y-px", className)}
            style={{
                background: "#1e1e22", // white-100 over gray-800
                ...style,
            }}
            {...props}
        >
            {children}
        </thead>
    )
}

const Body = ({ children, className, ...props }: HTMLAttributes<HTMLTableSectionElement>) => (
    <tbody
        className={classNames("w-full text-gray-100", "[&>tr:not(:last-child)]:h-[1px]", className)}
        {...props}
    >
        {children}
        {/* The Buffer Row: Because this is the only row without a height constraint,
            the browser is forced to dump all remaining h-full table space here. */}
        <tr aria-hidden="true" className="border-0 m-0 p-0">
            <td colSpan={100} className="border-0 p-0 m-0"></td>
        </tr>
    </tbody>
)

const Footer = ({ children, className, style, ...props }: HTMLAttributes<HTMLDivElement>) => {
    const ctx = useContext(TableContext)
    if (!ctx?.footerPortalEl) return null
    return createPortal(
        <div
            className={classNames("p-2 border-t border-gray-700", className)}
            style={{ background: "#1e1e22", ...style }}
            {...props}
        >
            {children}
        </div>,
        ctx.footerPortalEl
    )
}

const Row = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
    ({ children, className, ...props }, ref) => (
        <tr ref={ref} className={classNames(className)} {...props}>
            {children}
        </tr>
    )
)
Row.displayName = "Row"

const Header = ({ children, className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) => (
    <th
        className={classNames(
            "text-left font-normal py-2.5 pl-4 gap-1 whitespace-nowrap border-b border-gray-700",
            className
        )}
        {...props}
    >
        {children}
    </th>
)

const Cell = ({ children, className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) => (
    <td
        className={classNames("py-3 pl-4 whitespace-nowrap border-b border-gray-700", className)}
        {...props}
    >
        {children}
    </td>
)

const SortIndicator = ({ sortState }: { sortState: "ASC" | "DESC" | "none" }) => {
    if (sortState === "none") return null
    return (
        <Icons.SmallIcon
            Icon={Icons.ArrowUp}
            className={classNames("text-blue-500", {
                "rotate-180": sortState === "DESC",
            })}
        />
    )
}

export const Table = Object.assign(TableRoot, {
    Head,
    Body,
    Footer,
    Row,
    Header,
    Cell,
    SortIndicator,
})

export const TablePure = Object.assign(TableRootPure, {
    Head,
    Body,
    Footer,
    Row,
    Header,
    Cell,
    SortIndicator,
})
